'use client';

import { Avatar, AvatarFallback } from '#/components/ui/avatar';
import { Button } from '#/components/ui/button';
import { Dialog } from '#/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu';
import useUserInfo from '#/data/useUserInfo';
import { LogoutDialog } from '#/modules/dialogs/logout-dialog';
import { StakingDialog } from '#/modules/dialogs/staking-dialog';
import getSsoProfileUrl from '#/utils/getSsoProfileUrl';
import { useAppKit } from '@reown/appkit/react';
import { Translation } from 'intl-lib';
import { LogOut, PackagePlus, PlusCircle, User } from 'lucide-react';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { keccak256, toBytes } from 'viem';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { AddAddressDialogContent } from '../dialogs/add-address-dialog';

type Props = {};

export function UserDropdown({}: Props) {
  const { address, chain, connector, isConnected } = useAccount();
  const modal = useAppKit();
  const { disconnect } = useDisconnect();
  const { connect } = useConnect();

  const { data, isLoading } = useUserInfo();
  const avatarFallback = useMemo(
    () => (data?.data.data?.name || data?.data.data?.email)?.charAt(0),
    [data]
  );
  const avatarColor = useMemo(
    () =>
      data?.data.data?.email != null
        ? `#${keccak256(toBytes(data?.data.data?.email ?? '')).substring(2, 8)}`
        : '#FFFFFF',
    [data]
  );

  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isStakingOpen, setIsStakingOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-accent-foreground"
          >
            <Avatar className="size-4">
              {/* <AvatarImage src={undefined} alt={data?.data.data?.email} /> */}
              <AvatarFallback
                className="uppercase bg-sidebar-primary"
                style={{ color: avatarColor }}
              >
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">
              {data?.data.data?.name || data?.data.data?.email}
            </span>
            {/* <ChevronsUpDown className="ml-auto" /> */}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
        //   side="top"
        //   className="w-(--radix-popper-anchor-width)"
        >
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <p className="text-sm">
                {data?.data.data?.name}(ID: {data?.data.data?.userId})
              </p>
              <p className="text-sm text-muted-foreground font-normal">
                {data?.data.data?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link href={getSsoProfileUrl()} target="_blank" prefetch={false}>
                <User className="h-4 w-4" />
                <span>
                  <Translation path="UserNav.EditProfile" />
                </span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setIsAddAddressOpen(true)}>
              <PlusCircle className="h-4 w-4" />
              <Translation path="PortfolioDashboard.AddAddress.ButtonLabel" />
              {/* <DropdownMenuShortcut>⌃⌥A</DropdownMenuShortcut> */}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setIsStakingOpen(true)}>
              <PackagePlus className="h-4 w-4" />
              <Translation path="my_staking.staking_dialog.trigger" />
              {/* <DropdownMenuShortcut>⌃⌥N</DropdownMenuShortcut> */}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setIsLogoutOpen(true)}>
            <LogOut className="h-4 w-4" />
            <Translation path="UserNav.SignOut.ButtonLabel" />
            {/* <DropdownMenuShortcut>⌃⌥Q</DropdownMenuShortcut> */}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LogoutDialog open={isLogoutOpen} onOpenChange={setIsLogoutOpen} />

      <Dialog open={isAddAddressOpen} onOpenChange={setIsAddAddressOpen}>
        <AddAddressDialogContent symbol="ETH" />
      </Dialog>

      <StakingDialog open={isStakingOpen} onOpenChange={setIsStakingOpen} />
    </>
  );
}
